import { useCallback, useEffect, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

/** The app's tooltip delay, since a hover popup usually stands in for one. */
const HOVER_OPEN_MS = 400
/** Grace period to cross the gap between the control and the popup it opened. */
const HOVER_CLOSE_MS = 250

interface UseHoverIntentOptions {
  onOpen: () => void
  onClose: () => void
  /** Whether the popup is already showing, however it got there. */
  isOpen: boolean
  disabled?: boolean
  openDelayMs?: number
  closeDelayMs?: number
}

/**
 * A popup that takes focus is the usual case, so a hover must not fire while
 * someone is mid-sentence in the chat input.
 */
function isEditingText(): boolean {
  const active = document.activeElement
  if (!(active instanceof HTMLElement)) return false
  return active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA'
}

/**
 * Rest the pointer on a control to reveal its popup, move away to dismiss it.
 *
 * Only a hover-opened popup closes on hover. One opened by a press or a
 * right-click is deliberate and stays until dismissed, so `onClose` fires only
 * for popups this hook opened. That means a re-entry over an already-open popup
 * must not re-arm the hover, or a press-opened popup would become dismissible by
 * walking the pointer across it. Every other close has to come back through
 * `cancel` or the same bookkeeping goes stale.
 *
 * Touch and pen have no hover state, so they're ignored. Those pointers reach
 * the popup through the press path.
 */
export function useHoverIntent({
  onOpen,
  onClose,
  isOpen,
  disabled,
  openDelayMs = HOVER_OPEN_MS,
  closeDelayMs = HOVER_CLOSE_MS,
}: UseHoverIntentOptions) {
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openedByHoverRef = useRef(false)

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const cancel = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
    openedByHoverRef.current = false
  }, [clearCloseTimer, clearOpenTimer])

  useEffect(() => cancel, [cancel])

  const scheduleClose = useCallback(() => {
    if (!openedByHoverRef.current) return
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      openedByHoverRef.current = false
      onClose()
    }, closeDelayMs)
  }, [clearCloseTimer, closeDelayMs, onClose])

  const handleAnchorPointerEnter = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'mouse') return
      clearCloseTimer()
      // Re-entering an open popup's anchor calls off a pending close, and
      // nothing more: whoever opened it still owns how it closes.
      if (disabled || isOpen) return
      clearOpenTimer()
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null
        // Checked here rather than on entry, because the pointer can settle
        // before the typing starts.
        if (isEditingText()) return
        openedByHoverRef.current = true
        onOpen()
      }, openDelayMs)
    },
    [clearCloseTimer, clearOpenTimer, disabled, isOpen, onOpen, openDelayMs],
  )

  const handleAnchorPointerLeave = useCallback(() => {
    clearOpenTimer()
    scheduleClose()
  }, [clearOpenTimer, scheduleClose])

  // A press has its own outcome; a hover that turns into one must not fire the
  // popup out from under it.
  const handleAnchorPointerDown = useCallback(() => {
    clearOpenTimer()
  }, [clearOpenTimer])

  return {
    cancel,
    anchorHandlers: {
      onPointerEnter: handleAnchorPointerEnter,
      onPointerLeave: handleAnchorPointerLeave,
      onPointerDown: handleAnchorPointerDown,
    },
    popupHandlers: {
      onPointerEnter: clearCloseTimer,
      onPointerLeave: scheduleClose,
    },
  }
}
